import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import axios from 'axios'

import { getSessionId } from '@/utils'

import { env } from './constants'

// Header key enum
enum HeaderKey {
  PLATFORM = 'PLATFORM',
  SESSION = 'SESSION',
  CASE = 'CASE',
  AUTHORIZATION = 'AUTHORIZATION',
  LANGUAGE = 'LANGUAGE',
}

// Header key constants
const HEADERS: Record<HeaderKey, string> = {
  [HeaderKey.PLATFORM]: 'platform',
  [HeaderKey.SESSION]: 'session',
  [HeaderKey.CASE]: 'case',
  [HeaderKey.AUTHORIZATION]: 'Authorization',
  [HeaderKey.LANGUAGE]: 'Accept-Language',
} as const

// Extended axios config type to include retry options
interface ExtendedAxiosConfig extends AxiosRequestConfig {
  retries?: number
  retryDelay?: number
  _retry?: boolean
}

// Default config
const DEFAULT_CONFIG: ExtendedAxiosConfig = {
  baseURL: env.BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {},
  retries: 3,
  retryDelay: 1000,
}

const httpService = axios.create(DEFAULT_CONFIG)

// Setup request interceptor
httpService.interceptors.request.use(
  (config) => {
    // You can modify config here before request is sent
    return config
  },
  async (error: AxiosError) => {
    return Promise.reject(error)
  },
)

// Setup response interceptor
httpService.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const config = error.config as ExtendedAxiosConfig

    // Implement retry logic
    if (config?.retries && !config._retry) {
      config._retry = true
      config.retries--

      // Wait before retrying
      await new Promise((resolve) =>
        setTimeout(resolve, config.retryDelay || DEFAULT_CONFIG.retryDelay),
      )
      return httpService(config)
    }

    return Promise.reject(error)
  },
)

const setupDefaultHeaders = (_httpService: AxiosInstance): void => {
  const sessionId = getSessionId() || ''
  setHeader(HeaderKey.PLATFORM, 'web')
  setHeader(HeaderKey.SESSION, sessionId)
}

// Header management functions
const setHeader = (key: HeaderKey, value: string): void => {
  httpService.defaults.headers.common[HEADERS[key]] = value
}

const removeHeader = (key: HeaderKey): void => {
  delete httpService.defaults.headers.common[HEADERS[key]]
}

const resetHeaders = (): void => {
  Object.values(HeaderKey).forEach((key) => {
    removeHeader(key)
  })
  setupDefaultHeaders(httpService)
}

setupDefaultHeaders(httpService)

export default httpService

export const setCaseIdToDefaultHeaders = (caseId: string): void => {
  setHeader(HeaderKey.CASE, caseId)
}

export const setAuthorizationToDefaultHeaders = (token: string): void => {
  setHeader(HeaderKey.AUTHORIZATION, token)
}

export { setHeader, removeHeader, resetHeaders, HEADERS, HeaderKey }
